import React from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, Volume2, Zap, Moon, Sun, Globe, 
  Shield, Key, Smartphone, Mail, User,
  AlertCircle, Check, X, Settings as SettingsIcon
} from 'lucide-react';
import { useWorkStore } from '../../store/work-buttons/workStore';
import { toast } from 'react-hot-toast';

export function Settings() {
  const { 
    user, 
    notifications, 
    soundEffects, 
    autoStatus,
    toggleSetting,
    theme,
    setTheme,
    clearActivities
  } = useWorkStore();

  const handleClearData = () => {
    if (confirm('Are you sure you want to clear all activity data?')) {
      clearActivities();
      toast.success('Activity data cleared');
    }
  };

  const handleExportData = () => {
    const data = {
      user,
      activities: localStorage.getItem('workbuttons_activities'),
      settings: {
        notifications,
        soundEffects,
        autoStatus,
        theme
      },
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workbuttons-export-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Data exported successfully');
  };

  return (
    <div className="space-y-6">
      {/* Profile Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <User size={20} />
          Profile
        </h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Name</label>
            <p className="text-gray-900 dark:text-white font-medium">{user?.name}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
            <p className="text-gray-900 dark:text-white font-medium">{user?.email}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Role</label>
            <p className="text-gray-900 dark:text-white font-medium capitalize">{user?.role}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Department</label>
            <p className="text-gray-900 dark:text-white font-medium">{user?.department}</p>
          </div>
        </div>
      </motion.div>

      {/* Preferences */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <SettingsIcon size={20} />
          Preferences
        </h3>
        <div className="space-y-4">
          <ToggleOption
            icon={<Bell size={18} />}
            label="Push Notifications"
            description="Get notified about important updates"
            enabled={notifications}
            onToggle={() => toggleSetting('notifications')}
          />
          <ToggleOption
            icon={<Volume2 size={18} />}
            label="Sound Effects"
            description="Play sounds when clicking buttons"
            enabled={soundEffects}
            onToggle={() => toggleSetting('soundEffects')}
          />
          <ToggleOption
            icon={<Zap size={18} />}
            label="Auto Status"
            description="Automatically update status based on calendar"
            enabled={autoStatus}
            onToggle={() => toggleSetting('autoStatus')}
          />
          <ToggleOption
            icon={theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}
            label="Dark Mode"
            description="Toggle dark/light theme"
            enabled={theme === 'dark'}
            onToggle={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          />
        </div>
      </motion.div>

      {/* Integrations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Globe size={20} />
          Integrations
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <IntegrationCard
            name="Slack"
            icon="💬"
            status="connected"
            description="Send updates to Slack channels"
          />
          <IntegrationCard
            name="Google Calendar"
            icon="📅"
            status="connected"
            description="Sync with your calendar"
          />
          <IntegrationCard
            name="Microsoft Teams"
            icon="👥"
            status="available"
            description="Connect to Teams workspace"
          />
          <IntegrationCard
            name="Jira"
            icon="📋"
            status="available"
            description="Update tickets automatically"
          />
        </div>
      </motion.div>

      {/* Data Management */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Shield size={20} />
          Data & Privacy
        </h3>
        <div className="space-y-4">
          <button
            onClick={handleExportData}
            className="w-full text-left px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Export Data</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Download all your WorkButtons data
                </p>
              </div>
              <Key size={20} className="text-gray-400" />
            </div>
          </button>
          
          <button
            onClick={handleClearData}
            className="w-full text-left px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-red-600 dark:text-red-400">Clear Activity Data</p>
                <p className="text-sm text-red-500 dark:text-red-300">
                  Remove all activity history
                </p>
              </div>
              <AlertCircle size={20} className="text-red-400" />
            </div>
          </button>
        </div>
      </motion.div>

      {/* About */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-md p-6 text-white"
      >
        <h3 className="text-lg font-semibold mb-2">About WorkButtons</h3>
        <p className="text-sm opacity-90 mb-4">
          Transform your workday with one-click automation. Built with love for modern teams.
        </p>
        <div className="flex items-center gap-4 text-sm">
          <span>Version 1.0.0</span>
          <span>•</span>
          <span>Made with React & Tailwind</span>
        </div>
      </motion.div>
    </div>
  );
}

interface ToggleOptionProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
}

function ToggleOption({ icon, label, description, enabled, onToggle }: ToggleOptionProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div className="text-gray-500 dark:text-gray-400">{icon}</div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{label}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        </div>
      </div>
      <button
        onClick={onToggle}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full transition-colors
          ${enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
            ${enabled ? 'translate-x-6' : 'translate-x-1'}
          `}
        />
      </button>
    </div>
  );
}

interface IntegrationCardProps {
  name: string;
  icon: string;
  status: 'connected' | 'available';
  description: string;
}

function IntegrationCard({ name, icon, status, description }: IntegrationCardProps) {
  const isConnected = status === 'connected';
  
  return (
    <div className={`
      p-4 rounded-lg border-2 transition-all cursor-pointer
      ${isConnected 
        ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}
    `}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        {isConnected ? (
          <Check size={18} className="text-green-500" />
        ) : (
          <X size={18} className="text-gray-400" />
        )}
      </div>
      <h4 className="font-medium text-gray-900 dark:text-white">{name}</h4>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
      <button className={`
        mt-3 text-sm font-medium
        ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}
      `}>
        {isConnected ? 'Connected' : 'Connect'}
      </button>
    </div>
  );
}